import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { ConfirmDialog } from '../components/ConfirmDialog/ConfirmDialog';
import './RaffleSettingsPage.css';

interface RaffleSettings {
  id: string;
  userId: string;
  logoUrl?: string;
  backgroundUrl?: string;
  createdAt: string;
  updatedAt: string;
}

type ConfirmAction = 'deleteLogo' | 'deleteBackground' | null;

export default function RaffleSettingsPage() {
  const [settings, setSettings] = useState<RaffleSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // Загрузка текущих настроек
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/raffle-settings', {
        credentials: 'include',
      });

      if (!res.ok) {
        if (res.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Не удалось загрузить настройки');
      }

      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка лого
  const handleLogoUpload = async (file: File) => {
    if (!file) return;

    // Проверка типа файла
    if (!file.type.match(/image\/(jpeg|jpg|png|gif)/)) {
      toast.warning('Only images allowed (JPG, PNG, GIF)');
      return;
    }

    // Проверка размера
    if (file.size > 500 * 1024) {
      toast.warning('File size must not exceed 500 KB');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/raffle-settings/upload-logo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        throw new Error('Не удалось загрузить лого');
      }

      const data = await res.json();
      setSettings(data);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Ошибка загрузки лого:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // Загрузка фона
  const handleBackgroundUpload = async (file: File) => {
    if (!file) return;

    // Проверка типа файла
    if (!file.type.match(/image\/(jpeg|jpg|png|gif)/)) {
      toast.warning('Only images allowed (JPG, PNG, GIF)');
      return;
    }

    // Проверка размера
    if (file.size > 1024 * 1024) {
      toast.warning('File size must not exceed 1 MB');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/raffle-settings/upload-background', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        throw new Error('Не удалось загрузить фон');
      }

      const data = await res.json();
      setSettings(data);
      toast.success('Background uploaded successfully!');
    } catch (error) {
      console.error('Ошибка загрузки фона:', error);
      toast.error('Failed to upload background');
    } finally {
      setUploading(false);
    }
  };

  // Удаление лого
  const handleDeleteLogo = async () => {
    try {
      const res = await fetch('/api/raffle-settings/logo', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Не удалось удалить лого');
      }

      const data = await res.json();
      setSettings(data);
      toast.success('Logo deleted');
    } catch (error) {
      console.error('Ошибка удаления лого:', error);
      toast.error('Failed to delete logo');
    }
  };

  // Удаление фона
  const handleDeleteBackground = async () => {
    try {
      const res = await fetch('/api/raffle-settings/background', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Не удалось удалить фон');
      }

      const data = await res.json();
      setSettings(data);
      toast.success('Background deleted');
    } catch (error) {
      console.error('Ошибка удаления фона:', error);
      toast.error('Failed to delete background');
    }
  };

  // Drag & Drop для лого
  const handleLogoDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  }, []);

  // Drag & Drop для фона
  const handleBackgroundDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleBackgroundUpload(file);
    }
  }, []);

  // Обработка подтверждения удаления
  const handleConfirmDelete = () => {
    if (confirmAction === 'deleteLogo') {
      handleDeleteLogo();
    } else if (confirmAction === 'deleteBackground') {
      handleDeleteBackground();
    }
    setConfirmAction(null);
  };

  if (loading) {
    return (
      <div className="raffle-settings-page">
        <h1>Raffle Settings</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="raffle-settings-page">
      <h1>🎨 Raffle Design Settings</h1>
      <p className="raffle-settings-description">
        Upload custom logo and background to personalize your raffles
      </p>

      <div className="raffle-settings-grid">
        {/* Logo */}
        <div className="raffle-settings-card">
          <h2>🎁 Raffle Logo</h2>
          <p className="raffle-settings-hint">
            Recommended: 140x140px, PNG/JPG, max 500KB
          </p>

          <div
            className="upload-zone"
            onDrop={handleLogoDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {settings?.logoUrl ? (
              <div className="preview">
                <img src={settings.logoUrl} alt="Logo" />
              </div>
            ) : (
              <div className="upload-placeholder">
                <p>📤 Drop file here</p>
                <p>or</p>
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleLogoUpload(file);
              }}
              disabled={uploading}
              id="logo-input"
              style={{ display: 'none' }}
            />
            <label htmlFor="logo-input" className="upload-button">
              {uploading ? 'Uploading...' : settings?.logoUrl ? 'Replace' : 'Choose file'}
            </label>
          </div>

          {settings?.logoUrl && (
            <button
              onClick={() => setConfirmAction('deleteLogo')}
              className="delete-button"
              disabled={uploading}
            >
              ❌ Delete logo
            </button>
          )}
        </div>

        {/* Background */}
        <div className="raffle-settings-card">
          <h2>🌈 Raffle Background</h2>
          <p className="raffle-settings-hint">
            Recommended: 800x600px, PNG/JPG, max 1MB
          </p>

          <div
            className="upload-zone"
            onDrop={handleBackgroundDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {settings?.backgroundUrl ? (
              <div className="preview">
                <img src={settings.backgroundUrl} alt="Background" />
              </div>
            ) : (
              <div className="upload-placeholder">
                <p>📤 Drop file here</p>
                <p>or</p>
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleBackgroundUpload(file);
              }}
              disabled={uploading}
              id="background-input"
              style={{ display: 'none' }}
            />
            <label htmlFor="background-input" className="upload-button">
              {uploading ? 'Uploading...' : settings?.backgroundUrl ? 'Replace' : 'Choose file'}
            </label>
          </div>

          {settings?.backgroundUrl && (
            <button
              onClick={() => setConfirmAction('deleteBackground')}
              className="delete-button"
              disabled={uploading}
            >
              ❌ Delete background
            </button>
          )}
        </div>
      </div>

      <div className="raffle-settings-info">
        <h3>ℹ️ How it works:</h3>
        <ul>
          <li>Uploaded logo and background will be used in all your raffles</li>
          <li>If not uploaded - default design will be used</li>
          <li>Files are stored on the server and available only to you</li>
        </ul>
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <ConfirmDialog
          title={confirmAction === 'deleteLogo' ? 'Delete logo?' : 'Delete background?'}
          message={
            confirmAction === 'deleteLogo'
              ? 'Are you sure you want to delete the logo? Default image will be used.'
              : 'Are you sure you want to delete the background? Default background will be used.'
          }
          icon="🗑️"
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
